import { useForm } from 'react-hook-form';
import { TimeoutError, trackError } from '#api/errors.ts';
import { sendCommand } from '#api/hosts.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { Code } from '#components/atoms/code/Code.tsx';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import type { Host } from '#dn-types/host.ts';
import { DebugModal } from './DebugModal.tsx';
import styles from './RestartDialog.module.css';

type Props = {
  onClose: () => void;
  onSuccess: (data: Record<string, unknown>) => void;
  host: Host;
  debugElement: HTMLElement | ((element: HTMLElement) => HTMLElement | null) | null;
};

/**
 * Restart is the one mutating command in the debug menu: it tears down every tunnel on the host
 * and rebinds the UDP port. Unlike the read-only commands, it confirms before sending, and
 * isSubmitting prevents a second restart from firing while the first is still in flight.
 */
export function RestartDialog({ onSuccess, onClose, host, debugElement }: Props) {
  const {
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm();

  const onSubmit = handleSubmit(async () => {
    try {
      const result = await sendCommand(host.id, 'Restart');
      onSuccess(result);
    } catch (e) {
      if (e instanceof TimeoutError) {
        setError('root.global', { message: 'Unable to contact dnclient' });
      } else {
        trackError(e);
        setError('root.global', { message: 'Something went wrong, please try again' });
      }
    }
  });

  // Closing while a restart is in flight would let a reopened dialog fire a duplicate restart
  // (a remount resets isSubmitting), so the dialog refuses to close until the request settles.
  function handleClose() {
    if (!isSubmitting) onClose();
  }

  return (
    <DebugModal onClose={handleClose} isOpen={true} portalElement={debugElement}>
      <form action="POST" onSubmit={onSubmit}>
        <p className="h5 mb-8">
          Restart Nebula on <Code className="wrap-anywhere">{host.name}</Code>?
        </p>
        <p className="p-md mb-24">
          Restarting tears down all tunnels on this host and rebinds its UDP port. Active connections will drop and
          re-establish once Nebula is back up.
        </p>
        <div className={styles.RestartDialog_buttons}>
          <Button variant="link" type="button" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="danger" type="submit" disabled={isSubmitting}>
            Restart Nebula
          </Button>
        </div>
        {errors.root?.global?.message && <ErrorMessage>{errors.root.global.message}</ErrorMessage>}
      </form>
    </DebugModal>
  );
}
